import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { armWakeBeep, checkWakeBeep } from '@/lib/wakeBeep';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Backend asleep (Emergent preview idle): only a FULL page reload triggers the
// platform's native "Wake Up Agent" screen — in-app fetches cannot wake it.
function looksLikeBackendAsleep(err) {
    if (!err) return false;
    if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return true;
    const s = err.response?.status;
    return s === 502 || s === 503 || s === 504;
}

export default function LoginPage() {
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    const [loginForm, setLoginForm] = useState({ username: '', password: '' });
    const [deviceName, setDeviceName] = useState(localStorage.getItem('sokuzai_device_name') || '');

    useEffect(() => {
        checkWakeBeep(() => toast.success('✅ Serveur prêt — vous pouvez vous connecter !', { duration: 8000 }));
    }, []);

    // Change password state
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);
    const [tempToken, setTempToken] = useState(null);

    // Session en cours avec mot de passe provisoire (redirigé par ProtectedRoute / intercepteur)
    useEffect(() => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
            const storedToken = localStorage.getItem('token');
            if (storedUser?.must_change_password && storedToken) {
                setLoginForm(f => ({ ...f, username: storedUser.username || f.username }));
                setTempToken(storedToken);
                setMustChangePassword(true);
            }
        } catch { /* ignore */ }
        if (new URLSearchParams(window.location.search).get('pwd') === '1') {
            toast.info('Votre mot de passe provisoire doit être remplacé avant de continuer.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!loginForm.username || !loginForm.password) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }
        
        setLoading(true);
        try {
            const userData = await login(loginForm.username, loginForm.password);
            if (userData.must_change_password) {
                const token = localStorage.getItem('token');
                setTempToken(token);
                setMustChangePassword(true);
                // Don't navigate, show change password form
                setLoading(false);
                return;
            }
            toast.success('Connexion réussie');
        } catch (error) {
            if (looksLikeBackendAsleep(error)) {
                armWakeBeep();
                toast.info('Serveur en veille — rechargement pour le réveiller… Un bip sonnera quand il sera prêt.');
                setTimeout(() => window.location.reload(), 1200);
            } else {
                toast.error(error.response?.data?.detail || 'Erreur de connexion');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (newPassword.length < 8) {
            toast.error('Le mot de passe doit contenir au moins 8 caracteres');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        setChangingPassword(true);
        try {
            await api.post('/auth/change-password', { new_password: newPassword }, {
                headers: { Authorization: `Bearer ${tempToken}` }
            });
            toast.success('Mot de passe modifie');
            // Re-login with new password
            const uname = loginForm.username || JSON.parse(localStorage.getItem('user') || '{}').username;
            await login(uname, newPassword);
            setMustChangePassword(false);
        } catch {
            toast.error('Erreur lors du changement de mot de passe');
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div 
                className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
                style={{ 
                    backgroundImage: 'url(https://images.pexels.com/photos/16688015/pexels-photo-16688015.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1280&dpr=1)' 
                }}
            >
                <div className="absolute inset-0 bg-slate-900/60" />
                <div className="relative z-10 flex flex-col justify-end p-12 text-white">
                    <h1 className="font-primary text-4xl font-bold mb-4">
                        Gestion simplifiée de vos commandes et inventaires
                    </h1>
                    <p className="text-lg text-white/80">
                        Sokuzai vous permet de gérer efficacement vos stocks sur plusieurs sites, 
                        suivre vos commandes et analyser votre consommation.
                    </p>
                </div>
            </div>

            {/* Right side - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center lg:justify-start p-8 lg:pl-16 bg-slate-50 relative overflow-hidden" style={{ paddingBottom: '12vh' }}>
                {/* Logo Y Concept en filigrane */}
                <div className="absolute pointer-events-none" style={{ bottom: '5%', right: '2%' }}>
                    <img 
                        src="/logo-yconcept.png" 
                        alt="" 
                        className="select-none"
                        style={{ width: '320px', opacity: 0.06 }}
                        draggable={false}
                    />
                </div>

                <div className="w-full max-w-sm relative z-10">
                    {/* Header with Sokuzai logo */}
                    <div className="flex items-center mb-8">
                        {/* Sokuzai Logo */}
                        <div className="flex items-center gap-3">
                            <img
                                src="/sokuzai-icon.png"
                                alt="Sokuzai"
                                className="w-12 h-12 rounded-xl object-contain"
                            />
                            <div>
                                <h2 className="font-primary text-2xl font-bold text-slate-900">Sokuzai</h2>
                                <p className="text-sm text-slate-500">Gestion multi-site</p>
                            </div>
                        </div>
                    </div>

                    {/* Login / Change Password Form */}
                    <div className="bg-white/30 backdrop-blur-sm rounded-xl border border-slate-200/30 p-6 shadow-sm">
                        {!mustChangePassword ? (
                        <>
                        <h3 className="font-primary text-xl font-semibold mb-6">Bienvenue</h3>
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="login-username">Nom d'utilisateur</Label>
                                <Input
                                    id="login-username"
                                    type="text"
                                    placeholder="Identifiant"
                                    value={loginForm.username}
                                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                                    data-testid="login-email-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="login-password">Mot de passe</Label>
                                <div className="relative">
                                    <Input
                                        id="login-password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={loginForm.password}
                                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="login-device-name" className="text-slate-500 text-xs">
                                    Nom de cet appareil <span className="text-slate-400">(optionnel — aide l'admin à l'identifier)</span>
                                </Label>
                                <Input
                                    id="login-device-name"
                                    type="text"
                                    placeholder="ex : Tablette cuisine Fuji"
                                    value={deviceName}
                                    onChange={(e) => {
                                        setDeviceName(e.target.value);
                                        localStorage.setItem('sokuzai_device_name', e.target.value);
                                    }}
                                    data-testid="login-device-name-input"
                                />
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full btn-primary"
                                disabled={loading}
                                data-testid="login-submit-btn"
                            >
                                {loading ? 'Connexion...' : 'Se connecter'}
                            </Button>
                        </form>
                        </>
                        ) : (
                        <>
                        <div className="flex flex-col items-center gap-2 mb-4">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                <Lock className="w-5 h-5 text-amber-600" />
                            </div>
                            <h3 className="font-primary text-xl font-semibold">Nouveau mot de passe</h3>
                            <p className="text-sm text-slate-500 text-center">Veuillez choisir votre propre mot de passe</p>
                        </div>
                        <form onSubmit={handleChangePassword} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Nouveau mot de passe</Label>
                                <Input
                                    type="password"
                                    placeholder="Min. 8 caracteres"
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    data-testid="new-password-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Confirmer</Label>
                                <Input
                                    type="password"
                                    placeholder="Confirmer le mot de passe"
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    data-testid="confirm-password-input"
                                />
                            </div>
                            {newPassword && confirmPassword && newPassword !== confirmPassword && (
                                <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                            )}
                            <Button 
                                type="submit" 
                                className="w-full btn-primary"
                                disabled={changingPassword || newPassword.length < 8 || newPassword !== confirmPassword}
                                data-testid="change-password-btn"
                            >
                                {changingPassword ? 'Enregistrement...' : 'Valider'}
                            </Button>
                        </form>
                        </>
                        )}
                    </div>

                    <p className="text-center text-xs text-slate-400 mt-2">
                        Contactez votre administrateur pour obtenir vos identifiants.
                    </p>
                </div>
            </div>
        </div>
    );
}
